'use client';

import Link from 'next/link';
import { useParams } from 'next/navigation';
import { i18n } from '@/localization/i18n-config';
import { forwardRef, useState, useEffect } from 'react';

const LocalizedLink = forwardRef(({ href, locale, ...props }, ref) => {
  const params = useParams();
  const currentLocale = locale || params.locale || i18n.defaultLocale;

  const [isExternal, setIsExternal] = useState(false);

  useEffect(() => {
    if (typeof href !== 'string') {
      setIsExternal(false);
      return;
    }

    if (href.startsWith('mailto:') || href.startsWith('tel:')) {
      setIsExternal(true);
      return;
    }

    if (!href.startsWith('http')) {
      setIsExternal(false);
      return;
    }

    try {
      const linkUrl = new URL(href);
      setIsExternal(linkUrl.hostname !== window.location.hostname);
    } catch (error) {
      setIsExternal(false);
    }
  }, [href]);

  const withLocale = (url) => {
    if (isExternal || typeof url !== 'string' || !url.startsWith('/')) {
      return url;
    }

    const segments = url.split('/').filter((segment) => segment);

    let localizedUrl = '';

    if (i18n.locales.includes(segments[0])) {
      if (currentLocale === i18n.defaultLocale) {
        segments.shift();
        localizedUrl = '/' + segments.join('/');
      } else {
        segments[0] = currentLocale;
        localizedUrl = '/' + segments.join('/');
      }
    } else {
      localizedUrl =
        currentLocale === i18n.defaultLocale ? url : `/${currentLocale}${url}`;
    }

    if (localizedUrl === '') {
      return currentLocale === i18n.defaultLocale ? '/' : `/${currentLocale}/`;
    }

    if (localizedUrl !== '/' && !localizedUrl.endsWith('/')) {
      localizedUrl += '/';
    }

    return localizedUrl;
  };

  const finalHref = withLocale(href);

  if (isExternal) {
    return (
      <a
        ref={ref}
        href={finalHref}
        target="_blank"
        rel="noopener noreferrer nofollow"
        {...props}
      />
    );
  }

  return <Link ref={ref} href={finalHref} {...props} />;
});

export default LocalizedLink;
